import { DndContext, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Checkbox, CheckboxTree, Search, useTranslationContext } from 'platform/components';
import { startTransition, useState } from 'react';
import { sort } from 'ramda';
import { useColumnHidingAllowed } from '../../../../store/selectors';
import { useColumnStates } from '../../../../store/selectors/useColumnStates';
import { useEagGridStore } from '../../../../store/useEagGridStore';
import type { EagColumn } from '../../../../types/EagColumn';
import type { EagColumnOrGroup } from '../../../../types/EagColumnGroup';
import type { EagColumnState } from '../../../../types/EagColumnState';
import { isEagColumnGroup } from '../../../../utils/isEagColumnGroup';
import { SortableColumnItem } from './components/SortableColumnItem/SortableColumnItem';
import { buildCheckboxTreeOptions } from './utils/buildCheckboxTreeOptions';
import { computeToggleAllState } from './utils/computeToggleAllState';
import { filterCheckboxTreeOptions, filterFlatColumns, getMatchingColumnKeys } from './utils/filterColumnsBySearch';
export type ColumnsPanelProps = {
  columns: EagColumn[];
  columnConfig?: EagColumnOrGroup[];
};
const DRAG_ACTIVATION_DISTANCE = 8;
export function ColumnsPanel(props: ColumnsPanelProps) {
  const t = useTranslationContext();
  const store = useEagGridStore();
  const columnStates = useColumnStates();
  const columnHidingAllowed = useColumnHidingAllowed();
  const [searchTerm, setSearchTerm] = useState('');
  const sensors = useSensors(useSensor(PointerSensor, {
    activationConstraint: {
      distance: DRAG_ACTIVATION_DISTANCE
    }
  }));
  const sortedColumnStates = sort((a: EagColumnState, b: EagColumnState) => a.order - b.order, columnStates);
  const columnByKey = new Map(props.columns.map(col => [col.key, col]));
  const columnStateByKey = new Map(columnStates.map(cs => [cs.key, cs]));
  const sortedColumns = sortedColumnStates.map(state => columnByKey.get(state.key)).filter((col): col is EagColumn => col !== undefined);
  const hasGroupConfig = props.columnConfig && props.columnConfig.some(isEagColumnGroup);

  // Visible keys = all leaf column keys where hidden !== true
  const visibleKeys: string[] = [];
  for (const cs of columnStates) {
    if (!(cs.hidden ?? false)) {
      visibleKeys.push(cs.key);
    }
  }
  const lowerSearch = searchTerm.toLowerCase();
  const matchingKeys = getMatchingColumnKeys(props.columnConfig, props.columns, columnStates, lowerSearch);
  const toggleState = computeToggleAllState(columnStates, matchingKeys);
  const handleSearchChange = (value: string | null) => {
    startTransition(() => {
      setSearchTerm(value ?? '');
    });
  };
  const handleToggleAll = () => {
    const shouldHide = toggleState.isAllEnabled;
    const keysToUpdate = new Set(toggleState.visibleColumnKeys);
    store.setColumnStates(columnStates.map(cs => keysToUpdate.has(cs.key) ? {
      ...cs,
      hidden: shouldHide
    } : cs));
  };
  const handleCheckboxTreeChange = (nextVisible: string[] | null) => {
    const nextVisibleSet = new Set(nextVisible ?? []);
    // Batch-update all leaf columns in a single store mutation
    store.setColumnStates(columnStates.map(cs => {
      const shouldBeVisible = nextVisibleSet.has(cs.key);
      const currentlyVisible = !(cs.hidden ?? false);
      if (shouldBeVisible !== currentlyVisible) {
        return {
          ...cs,
          hidden: !shouldBeVisible
        };
      }
      return cs;
    }));
  };
  const handleDragEnd = (event: DragEndEvent) => {
    const activeId = event.active.id as string;
    const overId = event.over?.id as string | undefined;
    if (!overId || activeId === overId) {
      return;
    }
    store.reorderColumnsByKey(activeId, overId);
  };
  const panelHeader = <div className="flex flex-col gap-3">
      <div className="px-4 pt-3 pb-2 flex-shrink-0">
        <Search value={searchTerm || null} onChange={handleSearchChange} data-testid="columns-panel-search" />
      </div>
      <div className="px-4 pb-2 flex-shrink-0">
        <Checkbox label={t('page.datagrid.labels.toggleAllColumns')} value={toggleState.isAllEnabled} isIndeterminate={toggleState.isIndeterminate} onChange={handleToggleAll} isDisabled={!columnHidingAllowed} data-testid="columns-panel-toggle-all" />
      </div>
    </div>;
  if (hasGroupConfig) {
    const filteredOptions = filterCheckboxTreeOptions(buildCheckboxTreeOptions(props.columnConfig ?? []), lowerSearch);
    return <div className="flex flex-col h-full overflow-hidden">
        {panelHeader}
        <div className="flex-1 overflow-y-auto py-2 px-4">
          <CheckboxTree options={filteredOptions} value={visibleKeys} onChange={columnHidingAllowed ? handleCheckboxTreeChange : undefined} isDisabled={!columnHidingAllowed} />
        </div>
      </div>;
  }
  const filteredColumns = filterFlatColumns(sortedColumns, lowerSearch);
  const filteredIds = filteredColumns.map(col => col.key);
  const isDragDisabled = searchTerm.length > 0;
  return <div className="flex flex-col h-full overflow-hidden">
      {panelHeader}
      <div className="flex-1 overflow-y-auto py-2">
        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
          <SortableContext items={filteredIds} strategy={verticalListSortingStrategy}>
            {filteredColumns.map(column => {
            const columnState = columnStateByKey.get(column.key);
            if (!columnState) {
              return null;
            }
            return <SortableColumnItem key={column.key} column={column} columnState={columnState} onSetHidden={store.setColumnHidden} columnHidingAllowed={columnHidingAllowed} isDragDisabled={isDragDisabled} />;
          })}
          </SortableContext>
        </DndContext>
      </div>
    </div>;
}